import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApi } from "@core/api";
import SignatureCanvasComponent from "react-signature-canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/pro-light-svg-icons";

export type AgreementTemplate = {
  DocumentKey: string;
  Version: string;
  Title: string;
  EffectiveDate: string;
  BodyHtml: string;
  RequiresDrawnSignature: boolean;
};

export type ExecuteAgreementPayload = {
  DocumentKey: string;
  CustomerId?: number | null;
  SignerName: string;
  SignerTitle?: string | null;
  SignerRole: string;
  SignatureBase64?: string | null;
  RelatedEntityKey?: string | null;
  ExtraFields?: Record<string, string>;
};

type Props = {
  documentKey: string;
  customerId?: number | null;
  signerRole: string;
  relatedEntityKey?: string | null;
  open: boolean;
  onClose: () => void;
  onSigned: () => void;
  /** Extra form fields (e.g. facility name for IOU). */
  extraFieldDefs?: Array<{ key: string; label: string; required?: boolean }>;
};

const BORDER = "#d1d5db";
const INPUT =
  "h-9 w-full rounded border px-3 text-sm bg-white placeholder:text-gray-400";

/**
 * Scrollable agreement viewer + attestation + optional signature pad.
 * Portaled above the site header; black text (SPG body/dialog theme is white).
 */
export function AgreementSignModal({
  documentKey,
  customerId,
  signerRole,
  relatedEntityKey,
  open,
  onClose,
  onSigned,
  extraFieldDefs = [],
}: Props) {
  const api = useApi();
  const sigRef = useRef<SignatureCanvasComponent | null>(null);
  const [template, setTemplate] = useState<AgreementTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [extra, setExtra] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAttested(false);
    setSignerName("");
    setSignerTitle("");
    setExtra({});
    setLoading(true);

    // Special handling for "save_signature" - no template needed
    if (documentKey === "save_signature") {
      setTemplate({
        DocumentKey: "save_signature",
        Version: "1.0",
        Title: "Save Your Signature",
        EffectiveDate: new Date().toISOString().split("T")[0],
        BodyHtml: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <h2>Save Your Signature</h2>
            <p>Please draw your signature below. This signature will be saved and used on all prescription PDFs generated for you.</p>
            <p>This is a one-time process during your first login.</p>
          </div>
        `,
        RequiresDrawnSignature: true,
      });
      setLoading(false);
      return;
    }

    api.api
      .get<AgreementTemplate>(`/spg/agreements/templates/${encodeURIComponent(documentKey)}`)
      .then((res) => setTemplate(res.data))
      .catch((e) => setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load agreement"))
      .finally(() => setLoading(false));
  }, [open, documentKey, api.api]);

  const submit = useCallback(async () => {
    if (!template || !signerName.trim()) return;
    const isSaveSignature = documentKey === "save_signature";
    if (!isSaveSignature && !attested) return;
    for (const f of extraFieldDefs) {
      if (f.required && !extra[f.key]?.trim()) {
        setError(`${f.label} is required.`);
        return;
      }
    }

    let signature: string | null = null;
    if (template.RequiresDrawnSignature) {
      if (!sigRef.current || sigRef.current.isEmpty()) {
        setError("Please draw your signature.");
        return;
      }
      signature = sigRef.current.toDataURL("image/png");
    }

    setSubmitting(true);
    setError(null);
    try {
      const body: ExecuteAgreementPayload = {
        DocumentKey: documentKey,
        CustomerId: customerId ?? null,
        SignerName: signerName.trim(),
        SignerTitle: signerTitle.trim() || null,
        SignerRole: signerRole,
        SignatureBase64: signature,
        RelatedEntityKey: relatedEntityKey ?? null,
        ExtraFields: Object.keys(extra).length ? extra : undefined,
      };
      const res = await api.api.post<{ IsSuccess: boolean; Messages?: string[] }>(
        "/spg/agreements/execute",
        body,
      );
      if (!res.data?.IsSuccess) {
        throw new Error(res.data?.Messages?.join("; ") || "Signing failed");
      }
      onSigned();
      onClose();
    } catch (e: any) {
      const msg =
        e?.response?.data?.Messages?.join?.("; ")
        || e?.response?.data?.detail
        || e?.message
        || "Signing failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    template,
    attested,
    signerName,
    signerTitle,
    extra,
    extraFieldDefs,
    documentKey,
    customerId,
    signerRole,
    relatedEntityKey,
    api.api,
    onSigned,
    onClose,    sigRef,  ]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
      aria-modal="true"
      aria-labelledby="agreement-sign-title"
      data-testid="agreement-sign-modal"
      data-agreement-light="">
      {/* Inline styles beat theme [role=dialog] white-text rules (and survive CSS cache). */}
      <style>{`
        [data-testid="agreement-sign-modal"][data-agreement-light],
        [data-testid="agreement-sign-modal"][data-agreement-light] *:not(svg):not(path) {
          color: #000000 !important;
        }
        [data-testid="agreement-sign-modal"][data-agreement-light] input,
        [data-testid="agreement-sign-modal"][data-agreement-light] textarea {
          color: #000000 !important;
          background: #ffffff !important;
          border-color: #d1d5db !important;
          -webkit-text-fill-color: #000000 !important;
        }
        [data-testid="agreement-sign-modal"][data-agreement-light] button.agreement-sign-primary,
        [data-testid="agreement-sign-modal"][data-agreement-light] button[data-agreement-primary] {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          background: #66AF4E !important;
        }
        [data-testid="agreement-sign-modal"][data-agreement-light] button.agreement-sign-link {
          color: #66AF4E !important;
          -webkit-text-fill-color: #66AF4E !important;
          background: transparent !important;
        }
        [data-testid="agreement-sign-modal"][data-agreement-light] .agreement-sign-error {
          color: #991b1b !important;
        }
      `}</style>
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        style={{ color: "#000000" }}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-300 px-5 py-4">
          <div>
            <h2 id="agreement-sign-title" className="text-lg font-semibold" style={{ color: "#000000" }}>
              {template?.Title ?? "Agreement"}
            </h2>
            {template && documentKey !== "save_signature" && (
              <p className="mt-1 text-xs" style={{ color: "#000000" }}>
                Version {template.Version}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-gray-100"
            style={{ color: "#000000" }}
            aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4" style={{ color: "#000000" }}>
          {loading && (
            <div className="py-10 text-center text-sm" style={{ color: "#000000" }}>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading agreement…
            </div>
          )}
          {error && (
            <div className="agreement-sign-error mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {template && (
            <div
              className="agreement-sign-body max-w-none text-sm leading-relaxed"
              style={{ color: "#000000" }}
              dangerouslySetInnerHTML={{ __html: template.BodyHtml }}
            />
          )}
        </div>

        {template && (
          <div className="space-y-3 border-t border-gray-300 bg-gray-50 px-5 py-4" style={{ color: "#000000" }}>
            {extraFieldDefs.map((f) => (
              <label key={f.key} className="block text-sm" style={{ color: "#000000" }}>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide" style={{ color: "#000000" }}>
                  {f.label}
                </span>
                <input
                  className={INPUT}
                  style={{ borderColor: BORDER, color: "#000000", background: "#fff" }}
                  value={extra[f.key] ?? ""}
                  onChange={(e) => setExtra((p) => ({ ...p, [f.key]: e.target.value }))}
                />
              </label>
            ))}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm" style={{ color: "#000000" }}>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide" style={{ color: "#000000" }}>
                  Full name
                </span>
                <input
                  className={INPUT}
                  style={{ borderColor: BORDER, color: "#000000", background: "#fff" }}
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                />
              </label>
              <label className="block text-sm" style={{ color: "#000000" }}>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide" style={{ color: "#000000" }}>
                  Title (optional)
                </span>
                <input
                  className={INPUT}
                  style={{ borderColor: BORDER, color: "#000000", background: "#fff" }}
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                />
              </label>
            </div>
            {template.RequiresDrawnSignature && (
              <div style={{ color: "#000000" }}>
                <div
                  className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide"
                  style={{ color: "#000000" }}>
                  <span style={{ color: "#000000" }}>Signature</span>
                  <button
                    type="button"
                    className="agreement-sign-link normal-case font-normal underline"
                    onClick={() => sigRef.current?.clear()}>
                    Clear
                  </button>
                </div>
                <div className="rounded border bg-white" style={{ borderColor: BORDER }}>
                  <SignatureCanvasComponent
                    ref={(r) => { sigRef.current = r; }}
                    penColor="#000000"
                    canvasProps={{
                      className: "w-full h-28",
                      style: { width: "100%", height: 112 },
                    }}
                  />
                </div>
              </div>
            )}
            {documentKey !== "save_signature" && (
              <label className="flex items-start gap-2 text-sm" style={{ color: "#000000" }}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                />
                <span style={{ color: "#000000" }}>
                  I have read and agree to this agreement (version {template.Version}). My electronic
                  acceptance is legally binding.
                </span>
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded border px-4 text-sm"
                style={{ borderColor: BORDER, color: "#000000", background: "#fff" }}
                disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={submitting || !signerName.trim() || (documentKey !== "save_signature" && !attested)}
                className="agreement-sign-primary h-9 rounded px-4 text-sm font-semibold disabled:opacity-50"
                style={{ background: "#66AF4E", color: "#ffffff" }}
                data-agreement-primary="">
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    {documentKey === "save_signature" ? "Saving…" : "Signing…"}
                  </>
                ) : (
                  documentKey === "save_signature" ? "Save Signature" : "Sign & Accept"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
